import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const AlYasminahContact: React.FC<{ content?: any }> = ({ content }) => {
  const { t, t_api } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [messageText, setMessageText] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    } else if (!/^[a-zA-Z\u0600-\u06FF\s'-]{2,}$/.test(formData.name.trim())) {
      newErrors.name = "Name must contain only letters (min 2 characters).";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required.";
    } else if (formData.subject.trim().length < 3) {
      newErrors.subject = "Subject must be at least 3 characters.";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setStatus(null);
    setMessageText("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, division: "alyasminah" }),
      });

      if (res.ok) {
        setStatus("success");
        setMessageText("✅ Message sent successfully!");
        setFormData({ name: "", email: "", subject: "", message: "" });
        setErrors({});
        setTimeout(() => setStatus(null), 3000);
      } else {
        setStatus("error");
        setMessageText("❌ Failed to send message.");
      }
    } catch {
      setStatus("error");
      setMessageText("❌ Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans pb-12">
      <motion.div
        className="bg-white rounded-lg shadow-lg overflow-hidden border"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Form */}
          <div className="lg:w-3/5 p-8 md:p-12">
            <h3 className="text-2xl font-bold mb-8">{t_api("Send Us Email", "أرسل لنا البريد الإلكتروني")}</h3>

            {status === "success" && (
              <div className="mb-4 text-sm font-semibold text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
                {messageText}
              </div>
            )}
            {status === "error" && (
              <div className="mb-4 text-sm font-semibold text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                {messageText}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none ${errors.name ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.email')} *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none ${errors.email ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.subject')} *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none ${errors.subject ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                />
                {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.message')} *</label>
                <textarea
                  rows={5}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none ${errors.message ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                ></textarea>
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition duration-300 disabled:opacity-50"
              >
                {loading ? t('common.loading') : t('contact.send')}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="lg:w-2/5 bg-gray-50 border-l border-gray-100 p-8 md:p-12">
            <h3 className="text-2xl font-bold mb-8">{t_api("Get In Touch", "تواصل معنا")}</h3>
            <p className="text-gray-600 mb-10 leading-relaxed text-sm">
              {t_api(content?.contact_subtitle || "Professional real estate guidance is just a message away.", content?.contact_subtitle_ar)}
            </p>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="text-red-600"><MapPin className="w-5 h-5" /></div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t('contact.address')}</h4>
                  <p className="text-gray-600 text-sm">{t_api(content?.contact_address || "Al Nahda, Sharjah, UAE", content?.contact_address_ar)}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="text-red-600"><Phone className="w-5 h-5" /></div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t('contact.phone')}</h4>
                  <p className="text-gray-600 text-sm">{content?.contact_phone || "+971 6 524 8861"}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="text-red-600"><Mail className="w-5 h-5" /></div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t('contact.email')}</h4>
                  <p className="text-gray-600 text-sm">{content?.contact_email || "alyasminah@hamdanoman.com"}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="text-red-600"><Clock className="w-5 h-5" /></div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t_api("Working Hours", "ساعات العمل")}</h4>
                  <p className="text-gray-600 text-sm">{t_api(content?.contact_hours || "Sun - Thu, 10 AM – 6 PM", content?.contact_hours_ar || "الأحد - الخميس، 10 صباحًا - 6 مساءً")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AlYasminahContact;
